<template>
  <div class="q-pa-md">
    <div class="row no-wrap justify-between q-col-gutter-x-md">
      <div class="row items-center">
        <h1 class="text-h4 text-weight-medium">
          <!-- Store name -->
          {{
            store?.store_name && store.store_number
              ? `${store.store_name} ${store.store_number}`
              : 'Store not found'
          }}
        </h1>
        <div
          class="q-gutter-xs q-pl-sm"
          v-if="
            parseBool(store?.remote_flag) ||
            parseBool(store?.network_flag) ||
            (store && store.french_region_ind > 0)
          "
        >
          <!-- Icons for meta -->
          <q-avatar
            v-if="parseBool(store?.network_flag)"
            size="md"
            rounded
            color="grey-4"
            icon="hub"
          />
          <q-avatar
            v-if="parseBool(store?.remote_flag)"
            size="md"
            rounded
            color="grey-4"
            icon="hiking"
          />
          <q-avatar v-if="store && store.french_region_ind > 0" size="md" rounded color="grey-4">
            FR
          </q-avatar>
        </div>
      </div>
      <div class="text-center column">
        <q-badge v-if="activeData.active" color="running" class="q-py-xs text-body2">
          {{ storeItemsLabel('visiting') }} {{ useFormatTime(activeData.time!) }}
        </q-badge>
      </div>
    </div>
    <ModalStoreEndVisitWarning v-model="modalStoreEndVisitWarning" />
    <div class="q-mt-sm q-gutter-sm">
      <q-btn
        no-caps
        rounded
        v-if="storeStates.isThisTimer"
        @click="endVisit"
        color="primary"
        :label="formLabel('endStore')"
      />
      <q-btn
        no-caps
        rounded
        v-else-if="
          storeStates.noActiveTimer && storeStates.thisStoreInTimers && storeStates.showResume
        "
        @click="startVisit"
        color="primary"
        :label="formLabel('resumeStore')"
      />
      <q-btn
        no-caps
        rounded
        v-else
        @click="startVisit"
        color="primary"
        :label="formLabel('startStore')"
      />
    </div>
  </div>

  <q-tabs
    v-model="tab"
    class="text-grey"
    active-color="secondary"
    indicator-color="secondary"
    align="justify"
    narrow-indicator
  >
    <q-tab name="details" :label="storeItemsLabel('details')"></q-tab>
    <q-tab name="tasks" :label="storeItemsLabel('tasks')">
      <q-badge color="secondary" floating>
        {{ taskCount }}
      </q-badge>
    </q-tab>
    <q-tab name="product" :label="storeItemsLabel('productLines')" />
    <q-tab name="picklist" :label="storeItemsLabel('picklist')" />
    <q-tab name="recommendations" :label="storeItemsLabel('recommendations')" />
    <q-tab name="survey" :label="storeItemsLabel('survey')" />
  </q-tabs>
  <q-separator />
  <q-tab-panels keep-alive v-model="tab" animated>
    <q-tab-panel name="details">
      <!-- Store Info -->
      <StoreBase v-if="store" :store="store" :states="storeStates" :active-data="activeData" />
    </q-tab-panel>
    <q-tab-panel keep-alive name="tasks">
      <StoreTasks
        :key="`${storeID}`"
        :store-id="storeID"
        :is-fetching="isFetching"
        :is-native="isNative"
        :store-states="storeStates"
      />
    </q-tab-panel>
    <q-tab-panel keep-alive name="product">
      <filtering-data-display
        :table-data="productLinesData"
        :enable-filters="true"
        :display-type="displayType"
      />
    </q-tab-panel>
    <q-tab-panel name="recommendations">
      <div class="text-h6">{{ storeItemsLabel('recommendations') }}</div>
      <p v-html="storeRecommendations"></p>
    </q-tab-panel>
    <q-tab-panel keep-alive name="picklist">
      <StorePicklist key="picklist" :store-id="storeID" :is-fetching="isFetching" />
    </q-tab-panel>
    <q-tab-panel name="survey">
      <StoreRecaps :key="`${storeID}-store-recaps`" :store-id="storeID" />
    </q-tab-panel>
  </q-tab-panels>
</template>
<script setup lang="ts">
import { computed, ComputedRef, inject, onBeforeUpdate, reactive, Ref, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import StoreBase from '../components/StoreBase.vue'
import FilteringDataDisplay from '../components/FilteringDataDisplay.vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useTimers } from '../store/timers'
import { useUserStore } from '../store/useUserStore'
import { useInventory } from '../store/useInventory'
import { useStoreProfiles } from '../store/useStoreProfiles'
import { DisplayType } from '../types/components/data-rendering'
import { Capacitor } from '@capacitor/core'
import { useFormatTime } from '../composables/formatTime'
import ModalStoreEndVisitWarning from '../components/store_profile/ModalStoreEndVisitWarning.vue'
import StorePicklist from '../components/store_profile/StorePicklist.vue'
import StoreTasks from '../components/store_profile/StoreTasks.vue'
import StoreRecaps from '../components/store_profile/StoreRecaps.vue'
import { parseBool } from '../utils'
import { checkMaintenance } from '../components/ModalMaintenance.vue'

export type StoreStates = {
  isThisTimer: ComputedRef<boolean>
  noActiveTimer: ComputedRef<boolean>
  thisStoreInTimers: ComputedRef<boolean>
  showResume: Ref<boolean>
}

// Note: This is currently never `provide`d anywhere, it's used by Storybook to swap to card view
const displayType = inject<DisplayType>('displayType', 'table')

//translations
const { t } = useI18n({ useScope: 'global' })
const formLabel = (key: string) => t(`form.${key}`)
const storeItemsLabel = (key: string) => t(`storeItems.${key}`)

//util
const isNative = computed(() => Capacitor.isNativePlatform())

//get store data
const { getStores } = storeToRefs(useStoreProfiles())
const { getStore } = useStoreProfiles()
const storeID = ref(useRoute().params.id as string)
const storeData = ref(getStores.value.data)
const store = computed(() => {
  return getStore(storeID.value as string)
})
onBeforeUpdate(async () => {
  date.value = inject('date', ref(new Date())).value
  storeID.value = useRoute().params.id as string
  storeData.value = getStores.value.data
})
const activeData = computed(() => {
  if (getActiveParentTimer.value?.data.store_name === store.value?.store_name) {
    const { getCurrentParentTime } = storeToRefs(useTimers())
    return { active: true, time: getCurrentParentTime }
  } else {
    return { active: false, time: undefined }
  }
})

//timers
const date = inject('date', ref(new Date()))

const { addParentTimer, stopActiveParentTimer } = useTimers()
const startVisit = checkMaintenance(() => {
  //prevent starting the task if the rep has any other activity running
  if (getActiveParentTimer.value) {
    modalStoreEndVisitWarning.value = true
    return void 0
  }
  if (!isLocked.value) {
    const timestamp = new Date()
    timestamp.setFullYear(date.value.getFullYear())
    timestamp.setMonth(date.value.getMonth())
    timestamp.setDate(date.value.getDate())
    timestamp.setSeconds(0)
    timestamp.setMilliseconds(0)
    void addParentTimer(
      {
        type: 'store',
        data: {
          store_name: store.value!.store_name,
          store_id: storeID.value,
          store_number: store.value!.store_number,
        },
      },
      timestamp
    )
  }
})
const modalStoreEndVisitWarning = ref(false)
const endVisit = checkMaintenance(() => {
  //if a rep is logging time against a task, they should not be able to end this task
  if (getActiveChildTimer.value) {
    modalStoreEndVisitWarning.value = true
    return void 0
  }
  const timestamp = new Date()
  timestamp.setFullYear(date.value.getFullYear())
  timestamp.setMonth(date.value.getMonth())
  timestamp.setDate(date.value.getDate())
  timestamp.setSeconds(0)
  timestamp.setMilliseconds(0)
  stopActiveParentTimer({ date: timestamp })
  showResume.value = true
  setTimeout(() => {
    showResume.value = false
  }, 120000)
})
const { getActiveParentTimer, getActiveChildTimer, getAllParentTimers, isLocked } = storeToRefs(
  useTimers()
)
const isThisTimer = computed(() => {
  if (getActiveParentTimer.value && getActiveParentTimer.value.data.store_id) {
    return getActiveParentTimer.value?.data.store_id === storeID.value
  } else return false
})
const noActiveTimer = computed((): boolean => {
  return !getActiveParentTimer.value ? true : false
})
const thisStoreInTimers = computed((): boolean => {
  return getAllParentTimers.value.some((timer) => {
    return timer.type === 'store' && timer.data.store_id == storeID.value
  })
})

const showResume = ref(false)

const storeStates = reactive<StoreStates>({
  isThisTimer,
  noActiveTimer,
  thisStoreInTimers,
  showResume,
})

//tasks
const { getTaskData } = useStoreProfiles()
const taskData = computed(() => {
  return getTaskData(storeID.value as string)
})

const taskCount = computed(() => {
  if (!taskData.value) return 0
  else {
    return taskData.value.data.filter((task) => {
      return task.task_status_id != 9 && task.task_status_id != 22
    }).length
  }
})

//tabs
const tab = ref('tasks')
watch(storeID, () => {
  if (storeID.value) tab.value = 'tasks'
})
//settings for tasks
const isFetching = ref(false)

const user = storeToRefs(useUserStore())
const renderRecommendations = (data: string) => {
  return data.replaceAll('*', '<br />')
}
const storeRecommendations = computed(() => {
  if (store.value && user.language.value === 'fr_CA' && store.value.fr_recommendations) {
    return renderRecommendations(store.value.fr_recommendations as string)
  }
  if (store.value && store.value.en_recommendations) {
    return renderRecommendations(store.value.en_recommendations as string)
  }
  return ''
})

//Product line
const { getPartsByStoreID } = useInventory()
const productLinesData = computed(() => {
  //@ts-expect-error will not be in table
  return getPartsByStoreID(storeID.value)
})
</script>
